// Mirrors de-billing-service/src/utils/fee-calculation.ts (BIL-106 rounding).
// Currency decimal places per de-billing-service/src/utils/currency.ts.
const DECIMAL_PLACES: Record<string, number> = { MNT: 2, USD: 2, EUR: 2, CNY: 2, JPY: 0, KRW: 0 };
export const decimalPlacesFor = (currency: string): number => DECIMAL_PLACES[currency] ?? 2;

export interface FeeTier { fromAmount: string; toAmount: string | null; rate: string; }

export interface FeePreviewInput {
  baseAmount: number;
  feeCurrency: string;
  calculationMethod: "PERCENTAGE" | "FLAT" | "TIERED";
  feeRate?: string | null;
  flatFeeAmount?: string | null;
  tierConfig?: FeeTier[] | null;
  minFeeAmount?: string | null;
  maxFeeAmount?: string | null;
  // B10 — platform-fee breakdown. platformFeeRate is the decimal fraction from
  // GET /my/platform-fee-rate (e.g. "0.01" = 1%); null when no contract rate.
  // Min/max are the contract's TRANSACTION_FEE bounds — the backend
  // (resolve-tx-fee-rate) clamps the platform fee with them, so the preview must too.
  platformFeeRate?: string | null;
  platformFeeMinAmount?: string | null;
  platformFeeMaxAmount?: string | null;
  absorbPlatformFee?: boolean;
  // When false, the tenant does not charge the end customer at all → fee is 0.
  chargeEndCustomerFee?: boolean;
}

export interface FeePreviewResult {
  calculatedFee: number;
  finalFee: number;
  appliedTier: FeeTier | null;
  minApplied: boolean;
  maxApplied: boolean;
  error: string | null;
  // B10 — null when no platform rate is available (preview keeps a placeholder).
  platformFeeRate: number | null;
  platformFee: number | null;
  tenantNet: number | null;
  // Deduction model: the end customer pays the order amount; the fee is taken
  // OUT of it. customerPays is the order base; netToMerchant is base - fee.
  customerPays: number;
  netToMerchant: number;
}

// Round half to even, to N decimal places (matches Decimal ROUND_HALF_EVEN).
function roundHalfEven(value: number, places: number): number {
  const factor = Math.pow(10, places);
  const x = value * factor;
  const floor = Math.floor(x);
  const diff = x - floor;
  let rounded: number;
  if (diff > 0.5) rounded = floor + 1;
  else if (diff < 0.5) rounded = floor;
  else rounded = floor % 2 === 0 ? floor : floor + 1;
  return rounded / factor;
}

/**
 * Client-side fee preview. Uses native float arithmetic, so it may differ by up to
 * 1 in the last decimal place from the backend (which uses Decimal.js ROUND_HALF_EVEN).
 * For display/estimation only — the backend is authoritative for actual billing.
 */
export function calculateFeePreview(input: FeePreviewInput): FeePreviewResult {
  const places = decimalPlacesFor(input.feeCurrency);
  const base = input.baseAmount;

  // Tenant chose not to charge the end customer → no fee at all. The customer
  // pays the order amount and the whole base goes to the counterparty.
  if (input.chargeEndCustomerFee === false) {
    return {
      calculatedFee: 0, finalFee: 0, appliedTier: null, minApplied: false, maxApplied: false,
      error: null,
      platformFeeRate: null, platformFee: null, tenantNet: null,
      customerPays: base, netToMerchant: base
    };
  }

  let calculated = 0;
  let appliedTier: FeeTier | null = null;

  try {
    if (input.calculationMethod === "PERCENTAGE") {
      if (!input.feeRate) throw new Error("feeRate required");
      calculated = base * Number(input.feeRate);
    } else if (input.calculationMethod === "FLAT") {
      if (!input.flatFeeAmount) throw new Error("flatFeeAmount required");
      calculated = Number(input.flatFeeAmount);
    } else {
      const tiers = input.tierConfig ?? [];
      if (tiers.length === 0) throw new Error("tiers required");
      const tier = tiers.find(
        (t) => base >= Number(t.fromAmount) && (t.toAmount === null || base < Number(t.toAmount))
      );
      if (!tier) throw new Error("no tier matches base amount");
      calculated = base * Number(tier.rate);
      appliedTier = tier;
    }
  } catch (e) {
    return {
      calculatedFee: 0, finalFee: 0, appliedTier: null, minApplied: false, maxApplied: false,
      error: (e as Error).message,
      platformFeeRate: null, platformFee: null, tenantNet: null,
      customerPays: base, netToMerchant: base
    };
  }

  let final = calculated;
  let minApplied = false;
  let maxApplied = false;
  if (input.minFeeAmount) {
    const min = Number(input.minFeeAmount);
    if (final < min) { final = min; minApplied = true; }
  }
  if (input.maxFeeAmount) {
    const max = Number(input.maxFeeAmount);
    if (final > max) { final = max; maxApplied = true; }
  }

  const finalFee = roundHalfEven(final, places);

  // Deduction model: the end customer pays the order amount (base); the fee is
  // carved OUT of it and goes to the tenant. The counterparty receives the
  // remainder (base - fee). The fee is NOT a surcharge added on top.
  const customerPays = base;
  const netToMerchant = roundHalfEven(base - finalFee, places);

  // B10 — platform-fee breakdown. The platform's transaction fee is DeHub's cut,
  // levied on the order base and taken out of the tenant's fee. The tenant's net
  // income is its own fee minus the platform's cut.
  const platformRate = input.platformFeeRate != null && input.platformFeeRate !== ""
    ? Number(input.platformFeeRate)
    : null;
  let platformFee: number | null = null;
  let tenantNet: number | null = null;
  if (platformRate != null && !Number.isNaN(platformRate)) {
    let pf = base * platformRate;
    if (input.platformFeeMinAmount) {
      const pMin = Number(input.platformFeeMinAmount);
      if (pf < pMin) pf = pMin;
    }
    if (input.platformFeeMaxAmount) {
      const pMax = Number(input.platformFeeMaxAmount);
      if (pf > pMax) pf = pMax;
    }
    platformFee = roundHalfEven(pf, places);
    tenantNet = roundHalfEven(finalFee - platformFee, places);
  }

  return {
    calculatedFee: roundHalfEven(calculated, places),
    finalFee,
    appliedTier, minApplied, maxApplied, error: null,
    platformFeeRate: platformRate, platformFee, tenantNet,
    customerPays, netToMerchant
  };
}
